import React from "react";
import Highcharts from "highcharts";
import useOutsideClick from "../../lib/hooks/useOutsideClick";

declare module "highcharts" {
  interface PointOptionsObject {
    allowPointSelect?: boolean | null;
  }
}

const selectable = (point: Highcharts.Point) => {
  if (!("allowPointSelect" in point.series.options)) return false;
  if (!point.series.options.allowPointSelect) return false;
  return point.options.allowPointSelect !== false;
};

const unselectPoints = (
  chart: Highcharts.Chart,
  setReferencePoint: React.Dispatch<React.SetStateAction<Highcharts.Point | null>>,
) => {
  const firstPoint = chart.getSelectedPoints()[0];
  if (firstPoint) firstPoint.select(false);
  chart.tooltip.destroy();
  setReferencePoint(null);
};

const useSelectablePoints = (
  doIt: boolean,
  wrapperRef: React.RefObject<HTMLElement>,
  chartRef: React.RefObject<Highcharts.Chart>,
  range: boolean,
) => {
  const [referencePoint, setReferencePoint] = React.useState<Highcharts.Point | null>(null);
  const [previousSelectedPoints, setPreviousSelectedPoints] = React.useState<
    Highcharts.Point[] | null
  >(null);

  React.useLayoutEffect(() => {
    if (!doIt) return;
    const chart = chartRef.current;
    if (!chart) {
      throw new Error("chart not found");
    }
    const handleClick = (e: Highcharts.ChartClickEventObject) => {
      // play nice with misclicks when selecting multiple points
      const accumulating = e.ctrlKey || e.metaKey || e.shiftKey;
      if (!accumulating) unselectPoints(chart, setReferencePoint);
    };
    // this event does not trigger on point clicks and navigator clicks. Which work for us.
    Highcharts.addEvent(chart, "click", handleClick);
    return () =>
      Highcharts.removeEvent(
        chart,
        "click",
        handleClick as Highcharts.EventCallbackFunction<Highcharts.Chart>,
      );
  }, [chartRef, doIt]);

  useOutsideClick(wrapperRef, [wrapperRef], () => {
    if (!doIt) return;
    if (!chartRef.current) {
      throw new Error("chartRef empty");
    }
    unselectPoints(chartRef.current, setReferencePoint);
  });

  const handlePointClick = React.useCallback(
    (e: Highcharts.PointClickEventObject) => {
      if (!doIt) return;
      const pointClicked = e.point;
      if (!selectable(pointClicked)) return;
      const ranging = e.shiftKey;
      const accumulating = e.ctrlKey || e.metaKey;

      if (!ranging || !referencePoint) {
        setReferencePoint(pointClicked);
        setPreviousSelectedPoints(null);
        pointClicked.select(!pointClicked.selected, accumulating);
      } else if (referencePoint?.series?.name === e.point.series.name) {
        if (!range) return;

        const min = Math.min(referencePoint.x, pointClicked.x);
        const max = Math.max(referencePoint.x, pointClicked.x);
        let middlePoints: Highcharts.Point[] = [];

        // TODO: PERFORMANCE ENHANCEMENT ON POINT SELECTION
        // We look through the all series point to find matches, it can be optimized
        for (const point of referencePoint.series.data) {
          if (point && point.x >= min && point.x <= max && selectable(point)) {
            middlePoints = [...middlePoints, point];
            point.select(true, true);
          }
        }

        let excludedPoints;
        if (previousSelectedPoints) {
          excludedPoints = previousSelectedPoints.filter(point => !middlePoints.includes(point));
          excludedPoints.map(p => p.select(false, true));
        }
        setPreviousSelectedPoints([...middlePoints, pointClicked]);
      } else {
        setReferencePoint(pointClicked);
        pointClicked.select(!pointClicked.selected, accumulating);
      }

      pointClicked.series.chart.tooltip.refresh(pointClicked); // update tooltip content
      return false; // do not select/unselect clicked point (default HC behavior when allowPointSelect)
    },
    [doIt, referencePoint, range, previousSelectedPoints],
  );

  React.useLayoutEffect(() => {
    if (!doIt) return;
    const chart = chartRef.current;
    if (!chart) {
      throw new Error("chart not found");
    }
    Highcharts.addEvent(chart, "pointClick", handlePointClick);
    return () =>
      Highcharts.removeEvent(
        chart,
        "pointClick",
        handlePointClick as Highcharts.EventCallbackFunction<Highcharts.Chart>,
      );
  }, [chartRef, handlePointClick, doIt]);

  React.useLayoutEffect(() => {
    if (!doIt) return;
    const chart = chartRef.current;
    if (!chart) {
      throw new Error("no chart yet?");
    }
    const handleTooltipClick = () => setReferencePoint(null);
    Highcharts.addEvent(chart, "tooltipClick", handleTooltipClick);
    return () =>
      Highcharts.removeEvent(
        chart,
        "tooltipClick",
        handleTooltipClick as Highcharts.EventCallbackFunction<Highcharts.Chart>,
      );
  }, [chartRef, doIt]);
};

export default useSelectablePoints;
